import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEV_USER_ID_STORAGE_KEY } from './services/api';
import { platformApi, User } from './services/platformApi';

interface AuthContextValue {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshCurrentUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const notifyAuthChanged = () => {
  window.dispatchEvent(new Event('talegence-auth-changed'));
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshCurrentUser = useCallback(async () => {
    const hasIdentity =
      Boolean(window.localStorage.getItem('authToken')) ||
      Boolean(window.localStorage.getItem(DEV_USER_ID_STORAGE_KEY)) ||
      Boolean(process.env.REACT_APP_DEV_USER_ID);
    if (!hasIdentity) {
      setCurrentUser(null);
      return;
    }
    const user = await platformApi.getCurrentUser();
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    refreshCurrentUser()
      .catch(() => {
        window.localStorage.removeItem('authToken');
        setCurrentUser(null);
      })
      .finally(() => setLoading(false));
  }, [refreshCurrentUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await platformApi.login(email, password);
    window.localStorage.setItem('authToken', result.access_token);
    window.localStorage.removeItem(DEV_USER_ID_STORAGE_KEY);
    setCurrentUser(result.user);
    notifyAuthChanged();
    return result.user;
  }, []);

  const logout = useCallback(() => {
    window.localStorage.removeItem('authToken');
    window.localStorage.removeItem(DEV_USER_ID_STORAGE_KEY);
    setCurrentUser(null);
    notifyAuthChanged();
  }, []);

  const value = useMemo(
    () => ({ currentUser, loading, login, logout, refreshCurrentUser }),
    [currentUser, loading, login, logout, refreshCurrentUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
